import {Button, Modal, ProgressBar} from "react-bootstrap";
import {t} from "ttag";
import {useCallback, useId, useRef, useState} from "react";
import { createRoot } from "react-dom/client";

type UploadFilter = {
    regex: string,
    error: string
}

export const fileImageFilter: UploadFilter = {
    regex: "image/.*",
    error: "File must be an image"
}

export const filePdfFilter: UploadFilter = {
    regex: "application/pdf",
    error: "File must be a PDF"
}

function getFileType(file: Blob | File) {
    if (file.type) {
        return file.type;
    }
    if (file instanceof File) {
        let reg = /(?:\.([^.]+))?$/.exec(file.name)
        if (reg && reg.length >= 1) {
            return reg[1];
        }
    }
    return ''; // this is already the fallback
}

interface UploadFileOptions {
    typeFilter?: UploadFilter
    progressCallback?: (progress: number) => void
    cancelable?: boolean
}

type UploadResult = { id: string, previewUrl: string } | { error: string }

export async function uploadFile(file: Blob | File, options?: UploadFileOptions & { cancelable?: false }): Promise<UploadResult>
export async function uploadFile(file: Blob | File, options?: UploadFileOptions & { cancelable: true }): Promise<{ result: Promise<UploadResult>, cancel: () => void }>
export async function uploadFile(file: Blob | File, options?: UploadFileOptions): Promise<UploadResult | { result: Promise<UploadResult>, cancel: () => void }> {
    const { typeFilter, progressCallback, cancelable } = options || {};
    if(typeFilter) {
        if(!getFileType(file).match(typeFilter.regex)) {
            return { error: typeFilter.error }
        }
    }
    const baseUpload = await fetch(`/uploads`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: `filename=${encodeURIComponent(file instanceof File ? file.name : "blob")}&contentType=${encodeURIComponent(file.type)}` +
            `&byteCount=${file.size}`,
        credentials: 'same-origin'
    })
    if(!baseUpload.ok) {
        return { error: await baseUpload.text() }
    }
    const uploadInfo : { uploadId: string, target: string, previewUrl: string } = await baseUpload.json()

    // Note that we cannot use `fetch` here because even in 2026(!) fetch does not support progress
    const xhr = new XMLHttpRequest();
    const uploadPromise = new Promise<UploadResult>((resolve, reject) => {
        xhr.upload.onprogress = ((evt) => {
            if (evt.lengthComputable && progressCallback) {
                progressCallback(evt.loaded / evt.total)
            }
        })
        xhr.open('PUT', uploadInfo.target, true);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve({ id: uploadInfo.uploadId, previewUrl: uploadInfo.previewUrl });
            } else {
                resolve({ error: `Upload failed with status ${xhr.status}: ${xhr.responseText}` });
            }
        }
        xhr.onerror = (evt) => {
            reject({ error: `Upload failed: ${evt.type}` });
        }
        xhr.send(file);
    });

    if(cancelable) {
        return {
            result: uploadPromise,
            cancel: () => {
                xhr.abort();
            }
        }
    } else {
        return await uploadPromise
    }
}

export interface FileData { id: string, name: string, type: string, previewUrl: string, file: File }

function FileUploadDialog({onComplete, filter, label, text, uploadText, doUpload} : {
    onComplete: (file?: FileData) => void | Promise<void>,
    filter?: UploadFilter,
    label: string,
    text: string,
    uploadText: string,
    doUpload: boolean
}) {
    const elementId = useId();
    const [value, setValue] = useState<FileList | null>(null);
    const [errorText, setErrorText] = useState("");
    const [progress, setProgress] = useState<number | undefined>(undefined);
    const cancelFunctionRef = useRef<(() => void) | undefined>(undefined);

    const submitFile = useCallback(async () => {
        if(!value || value.length < 1) {
            onComplete(undefined)
            return;
        }
        const file = value[0];
        // If the fileType isn't valid, fall back on the file extension
        const fileType = getFileType(file);
        if (doUpload) {
            setProgress(-1);
            const uploadResult = await uploadFile(file, {
                typeFilter: filter,
                progressCallback: setProgress,
                cancelable: true
            });
            cancelFunctionRef.current = uploadResult.cancel
            const uploadData = await uploadResult.result;
            cancelFunctionRef.current = undefined;
            if ('error' in uploadData) {
                setProgress(undefined);
                setErrorText(uploadData.error);
                throw new Error(uploadData.error);
            } else {
                setErrorText("")
                try {
                    await onComplete({id: uploadData.id!, previewUrl: uploadData.previewUrl!, name: file.name, type: fileType, file});
                } catch (e) {
                    let message = 'Unknown Error'
                    if (e instanceof Error) message = e.message
                    setErrorText(message)
                } finally {
                    setProgress(undefined);
                }
            }
        } else {
            setErrorText("")
            try {
                await onComplete({id: '', previewUrl: '', name: file.name, type: fileType, file});
            } catch (e) {
                let message = 'Unknown Error'
                if (e instanceof Error) message = e.message
                setErrorText(message)
            }
        }
    }, [value])

    return <Modal show={true} onHide={() => {
        if(cancelFunctionRef.current) {
            cancelFunctionRef.current();
        }
        onComplete(undefined);
    }}>
    <Modal.Header closeButton>
    <Modal.Title>{label}</Modal.Title>
    </Modal.Header>
    <Modal.Body>
        <form onSubmit={() => { submitFile(); return false; }}>
            <div>
                <label htmlFor={elementId}>{text}</label>
                <input type="file" className="form-control" id={elementId} accept={filter?.regex} onChange={(e) => setValue(e.target.files)} />
                {errorText}
            </div>
        </form>
        {progress != undefined ? <ProgressBar now={progress < 0 ? 100 : progress * 100} animated={progress < 0} label={progress < 0 ? t`Uploading...` : `${Math.round(progress * 100)}%`} className={"mt-2"} /> : null}
    </Modal.Body>
    <Modal.Footer>
        <Button variant="secondary" onClick={() => {
            if(cancelFunctionRef.current) {
                cancelFunctionRef.current();
            }
            onComplete(undefined);
        }}>
        {progress !== undefined ? t`Cancel` : t`Close`}
    </Button>
    <Button variant="primary" onClick={() => submitFile()} disabled={progress != undefined || !value || value.length < 1}>
        {uploadText}
    </Button>
    </Modal.Footer>
    </Modal>
}

export function fileUploadDialog({label, text, buttonText = t`Upload`, onComplete, doUpload = true, filter} : {
    label: string,
    text: string,
    buttonText?: string,
    onComplete?: (file?: FileData) => void | Promise<void>,
    doUpload?: boolean
    filter?: UploadFilter
}) {
    let resolver : (file?: FileData) => void
    const returnedPromise = new Promise<FileData | undefined>(resolve => {
        resolver = resolve
    })

    const container = document.createElement("div");
    const root = createRoot(container);
    async function onCompleteWrapper(file?: FileData) {
        if(file && onComplete) {
            await onComplete(file);
        }
        root.unmount();
        container.remove();
        resolver(file)
    }

    root.render(<FileUploadDialog onComplete={onCompleteWrapper} label={label} filter={filter} text={text} uploadText={buttonText} doUpload={doUpload} />);
    return returnedPromise;
}